"use client"

import type React from "react"
import { useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Edit2, Plus, Save, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { toast } from "@/hooks/use-toast"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"

export interface VideoItem {
	id : string
	url : string
	title : string
	description : string
	// thumbnail : string
}

export default function VideoInputManager ( {
	                                            setVideos, videos
                                            } : {
	setVideos : React.Dispatch<React.SetStateAction<VideoItem[]>>,
	videos : VideoItem[]
} ) {
	// const [ videos, setVideos ] = useState<VideoItem[]> ( [] )
	const [ isFormOpen, setIsFormOpen ] = useState ( false )
	const [ editingId, setEditingId ] = useState<string | null> ( null )

	const [ formData, setFormData ] = useState<Omit<VideoItem, "id">> ( {
		url: "",
		title: "",
		description: "",
		// thumbnail: "",
	} )


	// const fileInputRef = useRef<HTMLInputElement> ( null )

	const resetForm = () => {
		setFormData ( {
			url: "",
			title: "",
			description: "",
			// thumbnail: "",
		} )
		setEditingId ( null )
	}

	const handleFormOpen = () => {
		setIsFormOpen ( true )
		resetForm ()
	}

	const handleFormClose = () => {
		setIsFormOpen ( false )
		resetForm ()
	}

	const handleInputChange = ( e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> ) => {
		const { name, value } = e.target
		setFormData ( ( prev ) => ( { ...prev, [ name ]: value } ) )
	}

	// const handleThumbnailChange = ( e :
	// React.ChangeEvent<HTMLInputElement> ) => { const file =
	// e.target.files?.[ 0 ] if ( file ) { const reader = new
	// FileReader () reader.onload = () => { setFormData ( ( prev
	// ) => ( { ...prev, thumbnail: reader.result as string } ) )
	// } reader.readAsDataURL ( file ) } }

	const validateVideoUrl = ( url : string ) : boolean => {
		// Basic validation for common video platforms
		const videoPatterns = [
			/^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/,
			/^(https?:\/\/)?(www\.)?(vimeo\.com)\/.+$/,
			/^(https?:\/\/)?(www\.)?(dailymotion\.com|dai\.ly)\/.+$/,
			/^(https?:\/\/)?(player\.)?(twitch\.tv)\/.+$/,
			/^(https?:\/\/)?(www\.)?(facebook\.com|fb\.watch)\/.+$/,
			/\.mp4(\?.*)?$/,
			/\.webm(\?.*)?$/,
			/\.ogg(\?.*)?$/,
		]

		return videoPatterns.some ( ( pattern ) => pattern.test ( url ) )
	}

	const handleSubmit = ( e : React.FormEvent ) => {
		e.preventDefault ()

		if ( !formData.url || !formData.title ) {
			toast ( {
				title: "Missing required fields",
				description: "Please provide both a video URL and title.",
				variant: "destructive",
			} )
			return
		}

		if ( !validateVideoUrl ( formData.url ) ) {
			toast ( {
				title: "Invalid video URL",
				description: "Please enter a valid video URL from a supported platform.",
				variant: "destructive",
			} )
			return
		}

		if ( editingId ) {
			// Update existing video
			setVideos ( videos.map ( ( video ) => ( video.id === editingId ? {
				...formData, id: editingId
			} : video ) ) )
			toast ( {
				title: "Video updated",
				description: `"${ formData.title }" has been updated successfully.`,
			} )
		} else {
			// Add new video
			const newVideo : VideoItem = {
				...formData,
				id: Date.now ().toString (),
			}
			setVideos ( [ ...videos, newVideo ] )
			toast ( {
				title: "Video added",
				description: `"${ formData.title }" has been added to your collection.`,
			} )
		}

		setIsFormOpen ( false )
		resetForm ()
	}

	const handleEdit = ( video : VideoItem ) => {
		setFormData ( {
			url: video.url,
			title: video.title,
			description: video.description,
			// thumbnail: video.thumbnail,
		} )
		setEditingId ( video.id )
		setIsFormOpen ( true )
	}

	const handleDelete = ( id : string ) => {
		const videoToDelete = videos.find ( ( v ) => v.id === id )
		setVideos ( videos.filter ( ( video ) => video.id !== id ) )
		toast ( {
			title: "Video removed",
			description: `"${ videoToDelete?.title }" has been removed from your collection.`,
			variant: "default",
		} )
	}

	return (
			<div className="space-y-8 w-full" >
				<div className="flex justify-end" >
					<Button onClick={ handleFormOpen } className="group"
					        size="lg" disabled={ isFormOpen } >
						<Plus
								className="mr-2 h-4 w-4 transition-transform group-hover:rotate-90" />
						Add New Video
					</Button >
				</div >

				<Dialog
						open={ isFormOpen }
						onOpenChange={ ( open ) => {
							if ( !open ) handleFormClose ()
							setIsFormOpen ( open )
						} }
				>
					<DialogContent className="sm:max-w-[700px] p-0" >
						<DialogHeader className="p-6 pb-2" >
							<DialogTitle >{ editingId ? "Edit Video" : "Add New Video" }</DialogTitle >
							<DialogDescription >
								Fill in the details below
								to { editingId ? "update" : "add" } a video to
								your collection.
							</DialogDescription >
						</DialogHeader >

						<form
								className="space-y-4" >
							<div className="px-6" >
								<div
										className="grid grid-cols-1 md:grid-cols-2 gap-6" >
									<div className="space-y-4" >
										<div className="space-y-2" >
											<Label htmlFor="url" >
												Video URL <span
													className="text-red-500" >*</span >
											</Label >
											<Input
													id="url"
													name="url"
													value={ formData.url }
													onChange={ handleInputChange }
													placeholder="https://www.youtube.com/watch?v=..."
													required
											/>
										</div >

										<div className="space-y-2" >
											<Label htmlFor="title" >
												Title <span
													className="text-red-500" >*</span >
											</Label >
											<Input
													id="title"
													name="title"
													value={ formData.title }
													onChange={ handleInputChange }
													placeholder="Enter video title"
													required
											/>
										</div >

										<div className="space-y-2" >
											<Label
													htmlFor="description" >Description</Label >
											<Textarea
													id="description"
													name="description"
													value={ formData.description }
													onChange={ handleInputChange }
													placeholder="Enter video description"
													rows={ 4 }
											/>
										</div >
									</div >

									{/*<div className="space-y-4" >*/ }
									{/*	<div className="space-y-2" >*/ }
									{/*		<Label htmlFor="thumbnail" >Thumbnail*/ }
									{/*		                            Image</Label >*/ }
									{/*		<div className="flex items-center gap-2" >*/ }
									{/*			<Input*/ }
									{/*					ref={ fileInputRef }*/ }
									{/*					id="thumbnail"*/ }
									{/*					type="file"*/ }
									{/*					accept="image/*"*/ }
									{/*					onChange={ handleThumbnailChange }*/ }
									{/*					className="hidden"*/ }
									{/*			/>*/ }
									{/*			<Input*/ }
									{/*					name="thumbnail"*/ }
									{/*					value={ formData.thumbnail }*/ }
									{/*					onChange={ handleInputChange }*/ }
									{/*					placeholder="Enter image URL or upload"*/ }
									{/*			/>*/ }
									{/*			<Button type="button" variant="outline"*/ }
									{/*			        onClick={ () => fileInputRef.current?.click () } >*/ }
									{/*				Browse*/ }
									{/*			</Button >*/ }
									{/*		</div >*/ }
									{/*	</div >*/ }

									{/*	/!*<div className="mt-4" >*!/*/ }
									{/*	/!*	<Label >Thumbnail Preview</Label >*!/*/ }
									{/*	/!*	<div*!/*/ }
									{/*	/!*			className={ cn (*!/*/ }
									{/*	/!*					"mt-2 border rounded-md aspect-video flex items-center justify-center overflow-hidden bg-slate-100 dark:bg-slate-700",*!/*/ }
									{/*	/!*					formData.thumbnail ? "p-0" : "p-4",*!/*/ }
									{/*	/!*			) }*!/*/ }
									{/*	/!*	>*!/*/ }
									{/*	/!*		{ formData.thumbnail ? (*!/*/ }
									{/*	/!*				<img*!/*/ }
									{/*	/!*						src={ formData.thumbnail || "/placeholder.svg" }*!/*/ }
									{/*	/!*						alt="Thumbnail preview"*!/*/ }
									{/*	/!*						className="w-full h-full object-cover"*!/*/ }
									{/*	/!*						onError={ ( e ) => {*!/*/ }
									{/*	/!*							e.currentTarget.src = "/placeholder.svg?height=200&width=320"*!/*/ }
									{/*	/!*						} }*!/*/ }
									{/*	/!*				/>*!/*/ }
									{/*	/!*		) : (*!/*/ }
									{/*	/!*				<div*!/*/ }
									{/*	/!*						className="text-center text-slate-400" >*!/*/ }
									{/*	/!*					<div*!/*/ }
									{/*	/!*							className="text-4xl mb-2" >🖼️*!/*/ }
									{/*	/!*					</div >*!/*/ }
									{/*	/!*					<p >No thumbnail selected</p >*!/*/ }
									{/*	/!*				</div >*!/*/ }
									{/*	/!*		) }*!/*/ }
									{/*	/!*	</div >*!/*/ }
									{/*	/!*</div >*!/*/ }
									{/*</div >*/ }
								</div >
							</div >

							<DialogFooter className="p-6 pt-2" >
								<Button type="button" variant="outline"
								        onClick={ handleFormClose } >
									Cancel
								</Button >
								<Button type="button" onClick={ handleSubmit } >
									<Save className="mr-2 h-4 w-4" />
									{ editingId ? "Update Video" : "Save Video" }
								</Button >
							</DialogFooter >
						</form >
					</DialogContent >
				</Dialog >

				<div className="space-y-6" >
					<h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 border-b pb-2" >
						Your Video
						Collection { videos.length > 0 && `(${ videos && videos.length })` }
					</h2 >

					{ videos.length === 0 ? (
							<div
									className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg shadow" >
								<div className="text-5xl mb-4" >📹</div >
								<h3 className="text-xl font-medium text-slate-700 dark:text-slate-300 mb-2" >No
								                                                                             videos
								                                                                             added
								                                                                             yet</h3 >
								<p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto" >
									Start building your collection by clicking the
									"Add New Video" button above.
								</p >
								<Button type={ 'button' }
								        onClick={ handleFormOpen } >
									<Plus className="mr-2 h-4 w-4" />
									Add Your First Video
								</Button >
							</div >
					) : (
							<div
									className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" >
								<AnimatePresence >
									{ videos.map ( ( video ) => (
											<motion.div
													key={ video.id }
													initial={ { opacity: 0, scale: 0.9 } }
													animate={ { opacity: 1, scale: 1 } }
													exit={ {
														opacity: 0, scale: 0.9,
														transition: { duration: 0.2 }
													} }
													transition={ { duration: 0.3 } }
													layout
													className="h-full"
											>
												<Card
														className="h-full overflow-hidden hover:shadow-lg transition-shadow duration-300" >
													{/*<div*/ }
													{/*		className="relative aspect-video overflow-hidden bg-slate-100 dark:bg-slate-700" >*/ }
													{/*	{ video.thumbnail ? (*/ }
													{/*			<img*/ }
													{/*					src={ video.thumbnail || "/placeholder.svg" }*/ }
													{/*					alt={ video.title }*/ }
													{/*					className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"*/ }
													{/*					onError={ ( e ) => {*/ }
													{/*						e.currentTarget.src = "/placeholder.svg?height=200&width=320"*/ }
													{/*					} }*/ }
													{/*			/>*/ }
													{/*	) : (*/ }
													{/*			<div*/ }
													{/*					className="w-full h-full flex items-center justify-center text-slate-400" >*/ }
													{/*				<div className="text-center" >*/ }
													{/*					<div*/ }
													{/*							className="text-4xl mb-2" >🎬*/ }
													{/*					</div >*/ }
													{/*					<p >No thumbnail</p >*/ }
													{/*				</div >*/ }
													{/*			</div >*/ }
													{/*	) }*/ }
													{/*</div >*/ }
													<CardContent className="p-4" >
														<div className="space-y-3" >
															<h3 className="font-semibold text-lg line-clamp-1" >{ video.title }</h3 >

															<p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2" >
																{ video.description || "No description provided" }
															</p >

															<div
																	className="text-xs text-slate-400 dark:text-slate-500 truncate" >{ video.url }</div >

															<div
																	className="flex justify-between pt-2" >
																<Button variant="outline"
																        size="sm"
																        onClick={ () => handleEdit ( video ) } >
																	<Edit2
																			className="h-4 w-4 mr-1" /> Edit
																</Button >
																<Button variant="destructive"
																        size="sm"
																        onClick={ () => handleDelete ( video.id ) } >
																	<Trash2
																			className="h-4 w-4 mr-1" /> Remove
																</Button >
															</div >
														</div >
													</CardContent >
												</Card >
											</motion.div >
									) ) }
								</AnimatePresence >
							</div >
					) }
				</div >
			</div >
	)
}
